/**
 * 미션 제출 페이지 — 파일/링크 제출 폼
 *
 * 미디어(이미지/영상) 업로드와 링크 제출을 담당한다.
 * effectiveSubmissionType에 따라 미디어 전용, 링크 전용, 또는 둘 다 표시한다.
 */

import { useState } from 'react'
import type { MyApplicationResponse } from '@/lib/seedingPublicApi'
import { seedingPublicApi } from '@/lib/seedingPublicApi'

/** 업로드 중인 파일의 상태 */
export interface UploadingFile {
  file: File
  progress: number
  url?: string
  error?: string
}

interface SubmissionFormProps {
  application: MyApplicationResponse
  campaignId: string
  selectedRound: number
  effectiveSubmissionType: string // 'media' | 'link' | 'both'
  hasRejectedInRound: boolean
  previewMode: boolean
  // 미디어 상태
  mediaUrls: string[]
  setMediaUrls: (urls: string[] | ((prev: string[]) => string[])) => void
  uploadingFiles: UploadingFile[]
  setUploadingFiles: (files: UploadingFile[] | ((prev: UploadingFile[]) => UploadingFile[])) => void
  // 링크 상태
  seedingUrl: string
  setSeedingUrl: (url: string) => void
  // 변경 감지
  mediaChanged: boolean
  linkChanged: boolean
  // 미디어 뷰어 트리거
  onViewMedia: (urls: string[], index: number) => void
  // 데이터 갱신
  onApplicationUpdate: (app: MyApplicationResponse) => void
}

export function SubmissionForm({
  application,
  campaignId,
  selectedRound,
  effectiveSubmissionType,
  hasRejectedInRound,
  previewMode,
  mediaUrls,
  setMediaUrls,
  uploadingFiles,
  setUploadingFiles,
  seedingUrl,
  setSeedingUrl,
  mediaChanged,
  linkChanged,
  onViewMedia,
  onApplicationUpdate,
}: SubmissionFormProps) {
  // 미디어 제출 상태
  const [isSubmittingMedia, setIsSubmittingMedia] = useState(false)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [mediaSuccess, setMediaSuccess] = useState(false)

  // 링크 제출 상태
  const [isSubmittingLink, setIsSubmittingLink] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [linkSuccess, setLinkSuccess] = useState(false)

  // 파일 선택 및 업로드
  const handleFileSelect = async (files: FileList | null) => {
    if (!files || !application || previewMode) return

    const currentUploadingCount = uploadingFiles.length
    const newFiles = Array.from(files).map((file) => ({
      file,
      progress: 0,
      url: undefined as string | undefined,
      error: undefined as string | undefined,
    }))

    setUploadingFiles((prev) => [...prev, ...newFiles])

    for (let i = 0; i < newFiles.length; i++) {
      const fileEntry = newFiles[i]
      const fileIndex = currentUploadingCount + i

      try {
        const url = await seedingPublicApi.uploadFile(
          application.id,
          fileEntry.file,
          (progress) => {
            setUploadingFiles((prev) =>
              prev.map((f, idx) => idx === fileIndex ? { ...f, progress } : f),
            )
          },
        )

        setUploadingFiles((prev) =>
          prev.map((f, idx) => idx === fileIndex ? { ...f, url, progress: 100 } : f),
        )
        setMediaUrls((prev) => [...prev, url])
      } catch (err: any) {
        setUploadingFiles((prev) =>
          prev.map((f, idx) => idx === fileIndex ? { ...f, error: err.message || '업로드 실패' } : f),
        )
      }
    }
  }

  // 업로드된 파일 제거
  const handleRemoveFile = (index: number) => {
    const removedUrl = uploadingFiles[index].url
    setUploadingFiles((prev) => prev.filter((_, i) => i !== index))
    if (removedUrl) {
      setMediaUrls((prev) => prev.filter((url) => url !== removedUrl))
    }
  }

  // 미디어 저장 (신규 POST)
  const handleSaveMedia = async () => {
    if (!application) return
    if (mediaUrls.length === 0) {
      setMediaError('이미지 또는 영상을 하나 이상 업로드해주세요.')
      return
    }
    const stillUploading = uploadingFiles.some((f) => !f.url && !f.error)
    if (stillUploading) {
      setMediaError('파일 업로드가 완료될 때까지 기다려주세요.')
      return
    }

    setIsSubmittingMedia(true)
    setMediaError(null)
    setMediaSuccess(false)

    try {
      await seedingPublicApi.submitSeedingByPhone(application.id, {
        mission_type: 'media',
        media_urls: mediaUrls,
        round_number: selectedRound,
      })

      const appData = await seedingPublicApi.getMyApplicationByCampaignPhone(campaignId)
      onApplicationUpdate(appData)
      setMediaSuccess(true)
      setTimeout(() => setMediaSuccess(false), 3000)
    } catch (err: any) {
      setMediaError(err.message || '저장에 실패했어요.')
    } finally {
      setIsSubmittingMedia(false)
    }
  }

  // 링크 저장 (신규 POST)
  const handleSaveLink = async () => {
    if (!application) return
    if (!seedingUrl || !seedingUrl.trim()) {
      setLinkError('링크를 입력해주세요.')
      return
    }

    setIsSubmittingLink(true)
    setLinkError(null)
    setLinkSuccess(false)

    try {
      await seedingPublicApi.submitSeedingByPhone(application.id, {
        mission_type: 'link',
        mission_url: seedingUrl.trim(),
        round_number: selectedRound,
      })

      const appData = await seedingPublicApi.getMyApplicationByCampaignPhone(campaignId)
      onApplicationUpdate(appData)
      setLinkSuccess(true)
      setTimeout(() => setLinkSuccess(false), 3000)
    } catch (err: any) {
      setLinkError(err.message || '저장에 실패했어요.')
    } finally {
      setIsSubmittingLink(false)
    }
  }

  return (
    <div className="mt-6 space-y-3">
      {/* 미디어 제출 영역 (link 전용일 때 숨김) */}
      {effectiveSubmissionType !== 'link' && (
        <div className="border rounded-lg border-border p-4">
          <h3 className="text-base font-semibold text-on-surface mb-3">파일 제출</h3>

          {mediaSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
              저장되었어요.
            </div>
          )}

          {/* 업로드된 파일 그리드 */}
          {uploadingFiles.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mb-3">
              {uploadingFiles.map((file, index) => (
                <FilePreviewItem
                  key={index}
                  file={file}
                  index={index}
                  allUploadingFiles={uploadingFiles}
                  onRemove={handleRemoveFile}
                  onViewMedia={onViewMedia}
                />
              ))}
            </div>
          )}

          {/* 업로드 드롭존 */}
          {previewMode ? (
            <div className="border-2 border-dashed border-border rounded-lg p-4 text-center mb-3 bg-surface">
              <div className="text-outline-variant">
                <p className="font-medium">클릭 또는 드래그하여 업로드</p>
                <p className="text-sm mt-1">이미지, 영상 (여러 개 선택 가능)</p>
                <p className="text-xs text-amber-500 mt-2">미리보기에서는 파일 업로드가 지원되지 않습니다</p>
              </div>
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors mb-3"
              onClick={() => document.getElementById('file-input')?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5') }}
              onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5') }}
              onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); handleFileSelect(e.dataTransfer.files) }}
            >
              <input
                id="file-input"
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
              <div className="text-on-surface-variant">
                <p className="font-medium">클릭 또는 드래그하여 업로드</p>
                <p className="text-sm text-outline-variant mt-1">이미지, 영상 (여러 개 선택 가능)</p>
              </div>
            </div>
          )}

          {mediaError && (
            <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg text-error text-sm">
              {mediaError}
            </div>
          )}

          <button
            onClick={handleSaveMedia}
            disabled={
              previewMode ||
              isSubmittingMedia ||
              uploadingFiles.some((f) => !f.url && !f.error) ||
              mediaUrls.length === 0 ||
              !mediaChanged
            }
            className="w-full py-2 px-4 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors disabled:opacity-50"
          >
            {isSubmittingMedia
              ? '저장 중...'
              : hasRejectedInRound
                ? '재제출하기'
                : '제출하기'}
          </button>
        </div>
      )}

      {/* 링크 제출 영역 (media 전용일 때 숨김) */}
      {effectiveSubmissionType !== 'media' && (
        <div className="border rounded-lg border-border p-4">
          <h3 className="text-base font-semibold text-on-surface mb-3">링크 제출</h3>

          {linkSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
              저장되었어요.
            </div>
          )}

          <div className="mb-4">
            <input
              type="url"
              value={seedingUrl}
              onChange={(e) => setSeedingUrl(e.target.value)}
              placeholder="https://..."
              disabled={previewMode}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm disabled:bg-surface"
            />
            <p className="text-xs text-on-surface-variant mt-1">SNS/블로그에 올린 미션 URL을 입력해주세요.</p>
          </div>

          {linkError && (
            <div className="mb-4 p-3 bg-error/10 border border-error/30 rounded-lg text-error text-sm">
              {linkError}
            </div>
          )}

          <button
            onClick={handleSaveLink}
            disabled={previewMode || isSubmittingLink || !seedingUrl.trim() || !linkChanged}
            className="w-full py-2 px-4 text-sm bg-primary text-white rounded-lg font-medium hover:bg-primary-dim transition-colors disabled:opacity-50"
          >
            {isSubmittingLink
              ? '저장 중...'
              : hasRejectedInRound
                ? '재제출하기'
                : '제출하기'}
          </button>
        </div>
      )}
    </div>
  )
}

// ==================== 파일 프리뷰 아이템 ====================

function FilePreviewItem({
  file,
  index,
  allUploadingFiles,
  onRemove,
  onViewMedia,
}: {
  file: UploadingFile
  index: number
  allUploadingFiles: UploadingFile[]
  onRemove: (index: number) => void
  onViewMedia: (urls: string[], index: number) => void
}) {
  return (
    <div className="relative aspect-square bg-surface-low rounded-lg overflow-hidden">
      {file.url ? (
        <>
          <button
            type="button"
            onClick={() => {
              const allUrls = allUploadingFiles.filter((f) => f.url).map((f) => f.url!)
              const viewIndex = allUrls.indexOf(file.url!)
              onViewMedia(allUrls, viewIndex >= 0 ? viewIndex : 0)
            }}
            className="w-full h-full"
          >
            {file.file.type.startsWith('video/') || file.url?.match(/\.(mp4|mov|webm)$/i) ? (
              <video src={file.url} preload="metadata" muted className="w-full h-full object-cover" />
            ) : (
              <img src={file.url} alt="" className="w-full h-full object-cover" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
          >
            ×
          </button>
        </>
      ) : file.error ? (
        <div className="w-full h-full flex items-center justify-center bg-error/10 text-error text-xs p-2 text-center">
          {file.error}
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-2"></div>
          <span className="text-xs text-on-surface-variant">{file.progress}%</span>
        </div>
      )}
    </div>
  )
}
